import React, { useState, useCallback, createContext, useContext } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  message: string;
  variant: ToastVariant;
  duration?: number;
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (message: string, variant: ToastVariant, duration?: number) => string;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((message: string, variant: ToastVariant, duration = 5000) => {
    const id = `${Date.now()}-${Math.random()}`;
    const newToast: Toast = { id, message, variant, duration };
    setToasts((prev) => [...prev, newToast]);

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastContainerProps {
  toasts: Toast[];
  onRemove: (id: string) => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onRemove }) => {
  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 pointer-events-none">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
};

interface ToastItemProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onRemove }) => {
  const getVariantClasses = (variant: ToastVariant) => {
    switch (variant) {
      case 'success':
        return 'bg-gn-success-light border-gn-success text-gn-success';
      case 'error':
        return 'bg-gn-danger-light border-gn-danger text-gn-danger';
      case 'warning':
        return 'bg-gn-warning-light border-gn-warning text-gn-warning';
      case 'info':
        return 'bg-gn-blue-light border-gn-blue text-gn-blue';
    }
  };

  const getIcon = (variant: ToastVariant) => {
    switch (variant) {
      case 'success':
        return <CheckCircle size={20} className="text-gn-success" />;
      case 'error':
        return <AlertCircle size={20} className="text-gn-danger" />;
      case 'warning':
        return <AlertCircle size={20} className="text-gn-warning" />;
      case 'info':
        return <Info size={20} className="text-gn-blue" />;
    }
  };

  return (
    // The *-light variant colours are mostly transparent tints, so they sit on a solid surface
    <div className="pointer-events-auto rounded-lg bg-gn-surface shadow-gn-lg animate-in fade-in slide-in-from-top-2">
      <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${getVariantClasses(toast.variant)}`}>
        {getIcon(toast.variant)}
        <span className="flex-1 text-sm font-medium">{toast.message}</span>
        <button
          onClick={() => onRemove(toast.id)}
          className="flex-shrink-0 ml-2 hover:opacity-70 transition-opacity"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
};

export default ToastProvider;
